import React, { useEffect, useState, useCallback } from "react";
import api from "../api/api"
import PropTypes from 'prop-types';
import { useTranslation } from "react-i18next";

const ConfirmCollectInformationModal = ({ employer, refresh }) => {
    const { t } = useTranslation();
    
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [linksent, setLinksent] = useState(null);
    const [copySuccess, setCopySuccess] = useState(false);

    const getLinkApi = useCallback(async () => {
        if (!employer?.id) return;
        
        setError(null);
        setLoading(true);
        
        try {  
            const response = await api.post("/glink-info-employer/", { id: employer.id }); 
            setLinksent(response.data.link);
        } catch (error) {
            let errorMessage = "";
            const responseCode = error.response ? error.response.status : "Unknown";
            console.error("API Error - Response Code:", responseCode);
            
            if (error?.response?.data) {
                Object.entries(error.response.data).forEach(([key, values]) => {
                    errorMessage += `${values}\n`;
                });
            }
            
            if (!errorMessage) {
                errorMessage = "Failed to generate collection link. Please try again.";
            }
            
            setError(errorMessage);
        } finally {
            setLoading(false);
        }
    }, [employer?.id]);

    const handleCopyLink = async () => {
        if (!linksent) return;
        
        try {
            await navigator.clipboard.writeText(linksent);
            setCopySuccess(true);
            setTimeout(() => setCopySuccess(false), 2000); // Reset after 2 seconds
        } catch (err) {
            console.error('Failed to copy link:', err);
            // Fallback for older browsers
            const textArea = document.createElement('textarea');
            textArea.value = linksent;
            document.body.appendChild(textArea);
            textArea.select();
            document.execCommand('copy');
            document.body.removeChild(textArea);
            setCopySuccess(true);
            setTimeout(() => setCopySuccess(false), 2000);
        }
    };

    const closeModal = () => {
        const modal = window.bootstrap?.Modal?.getInstance(document.getElementById('confirmCollectModal'));
        if (modal) {
            modal.hide();
        }
    };
    // Reset states when employer changes
    useEffect(() => {
        setError(null);
        setLinksent(null);
        setCopySuccess(false);
    }, [employer]);

    // Generate link when modal opens and employer is available
    useEffect(() => {
        if (employer?.id) {
            getLinkApi();
        }
    }, [employer?.id, getLinkApi]);
    return (
        <div className="modal custom-modal modal-lg fade" id="confirmCollectModal" role="dialog">
            {employer ? (
                <div className="modal-dialog modal-dialog-centered modal-md">
                    <div className="modal-content">
                        <div className="modal-header border-0 pb-0">
                            <div className="form-header modal-header-title text-start mb-0">
                                <h4 className="mb-0">{t("Generating Link to Collect Information")}</h4>
                            </div>
                            <button type="button" className="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
                        </div>
                        
                        <div className="modal-body">
                            <div className="row">
                                <div className="col-md-12">
                                    <div className="form-group">
                                        {loading ? (
                                            <div className="text-center py-3">
                                                <i className="fa-solid fa-spinner fa-spin" style={{fontSize: "24px"}}></i>
                                                <p className="mt-2">{t("Generating collection link...")}</p>
                                            </div>
                                        ) : linksent ? (
                                            <div>
                                                <p className="mb-3">
                                                    {t("Please copy the link below and send it to")} <strong>{employer?.name ?? 'the employer'}</strong>
                                                </p>
                                                <div className="input-group mb-3">
                                                    <input 
                                                        type="text" 
                                                        className="form-control" 
                                                        value={linksent} 
                                                        readOnly 
                                                        style={{fontSize: "12px"}}
                                                    />
                                                    <button 
                                                        className="btn btn-outline-secondary" 
                                                        type="button" 
                                                        onClick={handleCopyLink}
                                                        disabled={!linksent}
                                                    >
                                                        {copySuccess ? (
                                                            <>
                                                                <i className="fa-solid fa-check text-success"></i> {t("Copied")}
                                                            </>
                                                        ) : (
                                                            <>
                                                                <i className="fa-solid fa-copy"></i> {t("Copy")}
                                                            </>
                                                        )}
                                                    </button>
                                                </div>
                                                <div className="alert alert-info d-flex align-items-center" role="alert">
                                                    <i className="fa-solid fa-clock text-info me-2"></i>
                                                    <small className="mb-0">
                                                        {t("Note: Once the employer submits the form, this link will expire.")}
                                                    </small>
                                                </div>
                                            </div>
                                        ) : error ? (
                                            <div className="text-center py-3">
                                                <i className="fa-solid fa-exclamation-triangle text-warning" style={{fontSize: "24px"}}></i>
                                                <p className="mt-2 text-danger">{error}</p>
                                            </div>
                                        ) : null}
                                    </div>
                                </div>
                            </div>
                        </div>
                        
                        <div className="modal-footer">
                            <button 
                                type="button" 
                                data-bs-dismiss="modal" 
                                className="btn btn-back cancel-btn me-2"
                            >
                                {t("Close")}
                            </button>
                            
                            {error && (
                                <button 
                                    type="button" 
                                    onClick={getLinkApi}
                                    className="btn btn-primary"
                                    disabled={loading}
                                >
                                    {loading ? (
                                        <>
                                            <i className="fa-solid fa-spinner fa-spin"></i> {t("Generating...")}
                                        </>
                                    ) : (
                                        t("Try Again")
                                    )}
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            ) : (
                <div className="modal-dialog modal-dialog-centered modal-md">
                    <div className="modal-content">
                        <div className="modal-body text-center py-4">
                            <i className="fa-solid fa-spinner fa-spin" style={{fontSize: "24px"}}></i>
                            <p className="mt-2">{t("Loading...")}</p>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

ConfirmCollectInformationModal.propTypes = {
    employer: PropTypes.object.isRequired,
    refresh: PropTypes.func.isRequired,
};

export default ConfirmCollectInformationModal;
